/**
 * s111 — descriptive, STABLE filename for the Secure Information File, so the executor can
 * identify it: `[slug(name)]-secure-file-[YYYY-MM-DD].oesif` (e.g. reade-barber-secure-file-2026-07-02.oesif).
 *
 * Name = the planner's registration full name (user.fullName). Date = the file's CREATION
 * date — the caller passes `secure_file_setup_at ?? new Date()`, so it's "today" at first
 * creation and the pinned creation day thereafter. Set once and never re-dated on later
 * saves: on Chrome/Edge subsequent saves reuse the existing handle (name fixed at creation);
 * on Safari/Firefox each save re-downloads, and deriving the date from the creation timestamp
 * keeps that name identical. A blank name omits the name segment (secure-file-YYYY-MM-DD.oesif).
 *
 * Consumer of the secure-file lib (imports only from its public index for FILE_EXTENSION).
 */
import { FILE_EXTENSION } from "@/lib/secure-file";

/** Filesystem-safe slug: strip accents, lowercase, collapse any non-alphanumeric run to a
 *  single hyphen, trim leading/trailing hyphens. Returns "" for a blank / punctuation-only name. */
export function slugifyName(name: string | null | undefined): string {
  return (name ?? "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "") // drop combining diacritics ("José"/"Peña" → "jose"/"pena")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Coerce to a valid Date. `secure_file_setup_at` arrives from the API as an ISO STRING
 *  (JSON has no Date type), so a bare `.getFullYear()` on it throws — accept string|Date and
 *  fall back to "today" on a falsy or unparseable value so the filename never blocks creation. */
function coerceDate(v: Date | string | null | undefined): Date {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? new Date() : v;
  if (!v) return new Date();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

/** Local-time YYYY-MM-DD (the planner's own date at creation). */
function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Build the descriptive `.oesif` filename from a name + creation date. Blank/unslugifiable
 * name → the generic `secure-file-YYYY-MM-DD.oesif` fallback (never blocks file creation).
 * `createdAt` accepts a Date OR an ISO string (the API shape) OR null/undefined (→ today).
 */
export function secureFileName(name: string | null | undefined, createdAt: Date | string | null | undefined): string {
  const slug = slugifyName(name);
  const date = formatDate(coerceDate(createdAt));
  const stem = slug ? `${slug}-secure-file-${date}` : `secure-file-${date}`;
  return `${stem}${FILE_EXTENSION}`; // FILE_EXTENSION already includes the leading dot

}
